import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation, useApolloClient } from '@apollo/client';
import { object, string, array, mixed } from 'yup';
import { toast } from 'components/global/CUK/Toast/Toast';
import { CREATE_CENTER_DOCUMENT, UPDATE_CENTER_DOCUMENT } from '../operations-gql/mutations';
import { FIND_CENTER_DOCUMENTS } from '../operations-gql/queries';

interface DocumentInput {
  name: string;
  document_type: string;
  content_type: string | undefined;
  sf_account_ids: string[];
  file?: {
    name: string;
    dataURL: string;
  } | string;
}
  /* eslint-disable @typescript-eslint/no-explicit-any */

export const buildSchema = (t: any, isEdit = false) =>
  object({
    name: string().required(t('documents.edition.validation.name_required')),
    type: string().required(t('documents.edition.validation.type_required')),
    file: mixed()
      .test('fileRequired', t('documents.edition.validation.file_required'), function(value) {
        if (isEdit) return true;
        return value !== null && value !== undefined;
      }),
    sf_account_ids: array()
      .of(string())
      .min(1, t('documents.edition.validation.sites_required'))
  }).required();
  /* eslint-disable @typescript-eslint/no-explicit-any */

export const useSave = (navigate: any) => {
  const { t } = useTranslation();
  const [loading, setLoading] = useState(false);
  const apolloClient = useApolloClient();
  const [createCenterDocument] = useMutation(CREATE_CENTER_DOCUMENT);
  const [updateCenterDocument] = useMutation(UPDATE_CENTER_DOCUMENT);
  /* eslint-disable @typescript-eslint/no-explicit-any */

  const save = async (data: any, id?: string) => {
    setLoading(true);
    try {
      const { name, type, file, sf_account_ids } = data;
      
      let contentType;
      if (file && file.type) {
        contentType = file.type;
      } else if (file) {
        const extension = file.split('.').pop()?.toLowerCase();
        contentType = `application/${extension}`;
      }

      const input: DocumentInput = {
        name,
        document_type: type,
        content_type: contentType,
        sf_account_ids,
      };

      if (file instanceof File) {
        input.file = {
          name: file.name,
          dataURL: await new Promise<string>((resolve) => {
            const reader = new FileReader();
            reader.onload = (e) => {
              const result = e.target?.result;
              if (typeof result === 'string') {
                resolve(result);
              }
            };
            reader.readAsDataURL(file);
          })
        };        
      } else if (typeof file === 'string') {
        input.file = file;
      }

      if (id) {
        await updateCenterDocument({ variables: { id, document: input } });
        toast.success(t('documents.edition.toaster.update_success'));
      } else {
        await createCenterDocument({ variables: { document: input } });
        toast.success(t('documents.edition.toaster.create_success'));
      }

      await apolloClient.refetchQueries({
        include: [FIND_CENTER_DOCUMENTS],
      });

      navigate({ pathname: '/documents/center' });
    } catch (error) {
      console.error('Error saving document:', error);
      toast.error(t('documents.edition.toaster.create_update_error'));
    } finally {
      setLoading(false);
    }
  };

  return { save, loading };
};
